import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import AxiosInstance from "../../services/networkServices";
import routePaths from "../../router/route-paths";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../utils/roleAccess";
import fitnessSVG from "../../assets/fitnessSVG.svg";
import foodSVG from "../../assets/foodSvg.svg";
import "./calendar.css";
import "./calendar-print.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ROW_HEIGHT = 42;
const ROW_GAP = 6;
const DATE_HEADER_HEIGHT = 28;

// Category hierarchy for consistent vertical positioning
const CATEGORY_HIERARCHY = {
  fashion: 1,
  makeup: 2,
  "make up": 2,
  beauty: 2,
  pets: 3,
  pet: 3,
  food: 4,
  cooking: 4,
  baking: 4,
  fitness: 5,
  workout: 5,
  exercise: 5,
};

const getCategoryPriority = (event) => {
  if (event.type === "contest") {
    return 100;
  }
  
  const categoryName = (event.category || "").toLowerCase();
  
  for (const [keyword, priority] of Object.entries(CATEGORY_HIERARCHY)) {
    if (categoryName.includes(keyword)) {
      return priority;
    }
  }
  
  return 50;
};

const getQuarterFromDate = (date) => {
  const month = date.month();
  const quarter = Math.floor(month / 3) + 1;
  return { year: date.year(), quarter };
};

const getQuarterStart = (year, quarter) => {
  const startMonth = (quarter - 1) * 3;
  return dayjs().year(year).month(startMonth).startOf("month");
};

const getQuarterEnd = (year, quarter) => {
  const endMonth = quarter * 3 - 1;
  return dayjs().year(year).month(endMonth).endOf("month");
};

const getCategoryLogo = (categoryName, defaultLogo) => {
  const lowerCaseCategory = (categoryName || "").toLowerCase();
  if (lowerCaseCategory.includes("fitness")) return fitnessSVG;
  if (lowerCaseCategory.includes("food")) return foodSVG;
  return defaultLogo;
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CalendarPrint = () => {
  const navigate = useNavigate();
  
  const currentQuarter = useMemo(() => {
    return getQuarterFromDate(dayjs());
  }, []);

  const [challenges, setChallenges] = useState([]);
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(0);
  const { canViewActivity, hasFeature } = usePermissions();
  const canSeeContests = hasFeature(PERMISSIONS.CONTESTS);
  // Restricted accounts without view-activity get no counts (null hides them)
  const countOrNull = (value) => (canViewActivity ? value || 0 : null);

  // Fetch challenges and contests
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch challenges using the same endpoint as main calendar
        const challengesResponse = await AxiosInstance.post("/category/challenge/list", {
          page: 1,
          limit: 500,
          sortBy: "startDate",
          sortOrder: 1,
        });
        if (challengesResponse.data.status) {
          setChallenges(challengesResponse.data.data.list);
        }

        // Fetch contests using the same endpoint as main calendar (skipped
        // for restricted accounts without the contests permission)
        if (canSeeContests) {
          const contestsResponse = await AxiosInstance.get("/contest?page=1&limit=500");
          if (contestsResponse.data.status) {
            setContests(contestsResponse.data.data.list);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [canSeeContests]);

  // Calculate cell width
  useEffect(() => {
    if (loading) return;

    let rafId = null;

    const updateCellWidth = () => {
      if (!gridRef.current) {
        rafId = requestAnimationFrame(updateCellWidth);
        return;
      }

      const gridWidth = gridRef.current.getBoundingClientRect().width;
      // Each cell takes 1/7 of the grid width in CSS grid
      if (gridWidth > 0) {
        setCellWidth(gridWidth / 7);
      } else {
        rafId = requestAnimationFrame(updateCellWidth);
      }
    };

    updateCellWidth();
    window.addEventListener("resize", updateCellWidth);
    return () => {
      window.removeEventListener("resize", updateCellWidth);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [loading]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const quarterStart = getQuarterStart(currentQuarter.year, currentQuarter.quarter);
    const quarterEnd = getQuarterEnd(currentQuarter.year, currentQuarter.quarter);
    
    const calendarStart = quarterStart.startOf("week");
    const calendarEnd = quarterEnd.endOf("week");
    
    const firstMonthOfQuarter = quarterStart.month();
    const firstMonthName = quarterStart.format("MMMM");
    
    const days = [];
    let current = calendarStart;
    let firstQuarterDayFound = false;
    let firstMonthLabelShown = false;
    
    while (current.isBefore(calendarEnd) || current.isSame(calendarEnd, "day")) {
      const isInQuarter = (current.isAfter(quarterStart, "day") || current.isSame(quarterStart, "day")) && 
                          (current.isBefore(quarterEnd, "day") || current.isSame(quarterEnd, "day"));
      
      const isFirstQuarterDay = !firstQuarterDayFound && current.isSame(quarterStart, "day");
      if (isFirstQuarterDay) {
        firstQuarterDayFound = true;
      }
      
      const isFirstMonthOfQuarter = current.month() === firstMonthOfQuarter && !firstMonthLabelShown;
      if (isFirstMonthOfQuarter) {
        firstMonthLabelShown = true;
      }
      
      days.push({ 
        date: current, 
        isCurrentMonth: isInQuarter,
        isMonthStart: current.date() === 1,
        isFirstQuarterDay: isFirstQuarterDay,
        isFirstMonthOfQuarter: isFirstMonthOfQuarter,
        monthName: current.format("MMMM"),
        firstMonthName: isFirstMonthOfQuarter ? firstMonthName : null
      });
      current = current.add(1, "day");
    }
    
    return days;
  }, [currentQuarter]);

  // Combine challenges and contests into events
  const allEvents = useMemo(() => {
    const events = [];

    // Add challenges
    challenges.forEach((challenge) => {
      if (!challenge.startDate || !challenge.endDate) return;
      events.push({
        id: challenge._id,
        type: "challenge",
        title: challenge.title,
        startDate: challenge.startDate,
        endDate: challenge.endDate,
        category: challenge.category?.name || "Others",
        logo: getCategoryLogo(challenge.category?.name, challenge.category?.logo),
        submissionCount: countOrNull(challenge.postsCount ?? challenge.postCount),
      });
    });

    // Add contests
    contests.forEach((contest) => {
      if (!contest.votingStartDate || !contest.votingEndDate) return;
      events.push({
        id: contest._id,
        type: "contest",
        title: contest.title,
        startDate: contest.votingStartDate,
        endDate: contest.votingEndDate,
        prize: contest.prize,
        logo: contest.logo,
        submissionCount: countOrNull(contest.entriesCount ?? contest.numberOfSubmissions),
      });
    });

    return events;
  }, [challenges, contests, canViewActivity]);

  // Filter events to visible quarter
  const visibleEvents = useMemo(() => {
    const quarterStart = getQuarterStart(currentQuarter.year, currentQuarter.quarter);
    const quarterEnd = getQuarterEnd(currentQuarter.year, currentQuarter.quarter);
    
    return allEvents.filter((event) => {
      const eventStart = dayjs(event.startDate);
      const eventEnd = dayjs(event.endDate);
      return (
        (eventStart.isBefore(quarterEnd) || eventStart.isSame(quarterEnd, "day")) &&
        (eventEnd.isAfter(quarterStart) || eventEnd.isSame(quarterStart, "day"))
      );
    });
  }, [allEvents, currentQuarter]);

  // Get event status colors
  const getEventStatus = (event) => {
    const now = dayjs();
    const start = dayjs(event.startDate);
    const end = dayjs(event.endDate);

    if (event.type === "contest") {
      if (now.isBefore(start)) {
        return { color: "#a855f7", borderColor: "#a855f7" };
      } else if (now.isAfter(end)) {
        return { color: "#ec4899", borderColor: "#ec4899" };
      } else {
        return { color: "#8b5cf6", borderColor: "#8b5cf6" };
      }
    } else {
      if (now.isBefore(start)) {
        return { color: "#f59e0b", borderColor: "#f59e0b" };
      } else if (now.isAfter(end)) {
        return { color: "#ef4444", borderColor: "#ef4444" };
      } else {
        return { color: "#3b82f6", borderColor: "#3b82f6" };
      }
    }
  };

  // Get event bar segments (split by week) - matching main calendar logic
  const getEventBarSegments = (event) => {
    const originalEnd = dayjs(event.endDate).startOf("day");
    let start = dayjs(event.startDate).startOf("day");
    let end = originalEnd;

    const calendarStart = calendarDays[0]?.date.startOf("day");
    const calendarEnd = calendarDays[calendarDays.length - 1]?.date.startOf("day");

    if (!calendarStart || !calendarEnd) return [];

    // Clamp to calendar bounds
    if (start.isBefore(calendarStart)) start = calendarStart;
    if (end.isAfter(calendarEnd)) end = calendarEnd;

    const segments = [];
    let currentStart = start;

    while (currentStart.isBefore(end) || currentStart.isSame(end, "day")) {
      const dayIndex = calendarDays.findIndex((d) =>
        d.date.startOf("day").isSame(currentStart, "day")
      );
      if (dayIndex === -1) break;

      const weekRow = Math.floor(dayIndex / 7);
      const startCol = dayIndex % 7;

      let endCol = startCol;
      let segmentEnd = currentStart;

      // Extend segment until end of week (Saturday = col 6) or end of event
      while (endCol < 6) {
        const nextDay = segmentEnd.add(1, "day");
        // Stop if next day would be after the event end
        if (nextDay.isAfter(end)) break;
        endCol++;
        segmentEnd = nextDay;
      }

      const isStart = currentStart.isSame(
        dayjs(event.startDate).startOf("day"),
        "day"
      );
      // Check if this segment ends on the actual event end date
      const isEnd = segmentEnd.isSame(originalEnd, "day") || 
                    segmentEnd.isSame(end, "day");

      segments.push({ weekRow, startCol, endCol, isStart, isEnd });
      currentStart = segmentEnd.add(1, "day");
    }

    return segments;
  };

  // Assign slots to events
  const eventSlots = useMemo(() => {
    const slots = new Map();
    const sortedEvents = [...visibleEvents].sort((a, b) => {
      const priorityA = getCategoryPriority(a);
      const priorityB = getCategoryPriority(b);
      if (priorityA !== priorityB) return priorityA - priorityB;
      return dayjs(a.startDate).diff(dayjs(b.startDate));
    });

    const weekSlots = new Map();

    sortedEvents.forEach((event) => {
      const segments = getEventBarSegments(event);
      let assignedSlot = -1;

      segments.forEach((seg) => {
        const weekKey = seg.weekRow;
        if (!weekSlots.has(weekKey)) {
          weekSlots.set(weekKey, []);
        }

        const weekOccupiedSlots = weekSlots.get(weekKey);
        const overlaps = [];

        weekOccupiedSlots.forEach(({ slot, startCol, endCol }) => {
          if (!(seg.endCol < startCol || seg.startCol > endCol)) {
            overlaps.push(slot);
          }
        });

        let slot = 0;
        while (overlaps.includes(slot)) {
          slot++;
        }

        if (assignedSlot === -1 || slot > assignedSlot) {
          assignedSlot = slot;
        }

        weekOccupiedSlots.push({
          slot,
          startCol: seg.startCol,
          endCol: seg.endCol,
          eventId: event.id,
        });
      });

      slots.set(event.id, assignedSlot);
    });

    return slots;
  }, [visibleEvents, calendarDays]);

  // Get row height
  const getRowHeight = (weekIndex) => {
    const weekEvents = visibleEvents.filter((event) => {
      const segments = getEventBarSegments(event);
      return segments.some((seg) => seg.weekRow === weekIndex);
    });

    if (weekEvents.length === 0) {
      return ROW_HEIGHT;
    }

    const maxSlot = Math.max(
      ...weekEvents.map((event) => eventSlots.get(event.id) || 0)
    );

    return DATE_HEADER_HEIGHT + (maxSlot + 1) * (ROW_HEIGHT + ROW_GAP);
  };

  // Get month starts in week
  const getMonthStartsInWeek = (weekIndex) => {
    const weekDays = calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7);
    const monthStarts = [];
    
    weekDays.forEach((day, idx) => {
      if (day.isMonthStart || day.isFirstQuarterDay || day.isFirstMonthOfQuarter) {
        const labelName = day.firstMonthName || day.monthName;
        if (!monthStarts.find(m => m.monthName === labelName)) {
          monthStarts.push({
            dayIndex: idx,
            monthName: labelName
          });
        }
      }
    });
    
    return monthStarts;
  };

  const getQuarterLabel = () => {
    const quarterNames = {
      1: "Q1 (Jan - Mar)",
      2: "Q2 (Apr - Jun)",
      3: "Q3 (Jul - Sep)",
      4: "Q4 (Oct - Dec)",
    };
    return `${currentQuarter.year} ${quarterNames[currentQuarter.quarter]}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="calendar-print-container">
      <Box className="calendar-print-header">
        <button 
          className="calendar-print-back-btn"
          onClick={() => navigate(routePaths.CALENDAR)}
        >
          <ArrowBack sx={{ fontSize: 18 }} />
          Back to Calendar
        </button>
        <Typography variant="h4" className="calendar-print-title">
          {getQuarterLabel()}
        </Typography>
        <div style={{ width: 140 }} /> {/* Spacer for centering */}
      </Box>

      <div className="gantt-calendar gantt-calendar-print">
        <div className="gantt-weekdays">
          {WEEKDAYS.map((day) => (
            <div key={day} className="gantt-weekday">{day}</div>
          ))}
        </div>

        <div className="gantt-grid" ref={gridRef}>
          {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map(
            (_, weekIndex) => {
              const monthStarts = getMonthStartsInWeek(weekIndex);
              
              return (
                <div
                  key={weekIndex}
                  className="gantt-week-row"
                  style={{ minHeight: getRowHeight(weekIndex) }}
                >
                  {calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map(
                          (day, dayIndex) => {
                      const hasMonthLabel = day.isMonthStart || day.isFirstQuarterDay || day.isFirstMonthOfQuarter;
                      const isPast = day.date.isBefore(dayjs(), "day");
                      // Show month name on 1st of any month, or on first occurrence of first month of quarter
                      const quarterStartDate = getQuarterStart(currentQuarter.year, currentQuarter.quarter);
                      const showMonthName = day.isMonthStart || day.isFirstMonthOfQuarter || (day.isFirstQuarterDay && day.date.month() === quarterStartDate.month());
                            return (
                              <div
                                key={dayIndex}
                                className={`gantt-day-cell ${!day.isCurrentMonth ? "other-month" : ""} ${isPast ? "past" : ""} ${hasMonthLabel ? "month-start" : ""}`}
                              >
                                <div className="gantt-date-wrapper">
                                  <span className="gantt-date-number">{day.date.date()}</span>
                                  {showMonthName && (
                                    <span className="gantt-month-name">{day.monthName}</span>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        )}

                  <div className="gantt-bars-overlay">
                    {visibleEvents.map((event) => {
                      const segments = getEventBarSegments(event);
                      const slot = eventSlots.get(event.id) || 0;
                      const statusInfo = getEventStatus(event);

                      return segments
                        .filter((seg) => seg.weekRow === weekIndex)
                        .map((seg, segIndex) => {
                          // Match main calendar bar positioning with padding offsets
                          const left = seg.startCol * cellWidth;
                          const width = (seg.endCol - seg.startCol + 1) * cellWidth;
                          const top = DATE_HEADER_HEIGHT + slot * (ROW_HEIGHT + ROW_GAP);

                          return (
                            <div
                              key={`${event.id}-${segIndex}`}
                              className={`gantt-bar ${seg.isStart ? "bar-start" : ""} ${seg.isEnd ? "bar-end" : ""} ${event.type === "contest" ? "bar-contest" : ""}`}
                              style={{
                                left: `${left + (seg.isStart ? 4 : 0)}px`,
                                width: `${width - (seg.isStart ? 4 : 0) - (seg.isEnd ? 4 : 0)}px`,
                                top: `${top}px`,
                                backgroundColor: hexToRgba(statusInfo.color, 0.55),
                                borderColor: statusInfo.borderColor,
                              }}
                            >
                              {/* Continuation circle at start */}
                              {!seg.isStart && (
                                <span 
                                  className="gantt-bar-continuation gantt-bar-continuation-start"
                                  style={{ borderColor: statusInfo.borderColor }}
                                />
                              )}
                              {seg.isStart && (
                                <>
                                  {event.logo && (
                                    <img src={event.logo} alt="" className="gantt-bar-logo" />
                                  )}
                                  <span className="gantt-bar-title">{event.title}</span>
                                  {event.submissionCount != null && (
                                    <span className="gantt-bar-count">({event.submissionCount})</span>
                                  )}
                                </>
                              )}
                              {/* Continuation circle at end */}
                              {!seg.isEnd && (
                                <span 
                                  className="gantt-bar-continuation gantt-bar-continuation-end"
                                  style={{ borderColor: statusInfo.borderColor }}
                                />
                              )}
                            </div>
                          );
                        });
                    })}
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    </Box>
  );
};

export default CalendarPrint;

